import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// generated with ChatGPT
import { useCallback, useEffect, useRef, } from 'react';
import { toast, ToastContainer, } from 'react-toastify/unstyled';
import { Backdrop, } from './Backdrop.js';
import { Button, } from './Button.js';
const DIALOG_TOAST_ID = 'application-dialog';
const DIALOG_CONTAINER_ID = 'application-dialog-container';
const DIALOG_TITLE_ID = 'application-dialog-title';
const DIALOG_BODY_ID = 'application-dialog-body';
const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
/**
 * react-toastify/unstyled does not provide the CSS animations used
 * by Toastify's default transition.
 *
 * A transition is still responsible for calling `done()` when the
 * toast leaves. This transition removes the toast immediately.
 *
 * `onRemoved` runs after Toastify's internal `deleteToast()` has
 * executed, allowing showDialog() to resolve without racing another
 * dialog using the same toast ID.
 */
function createImmediateTransition(onRemoved) {
    function ImmediateTransition({ children, isIn, done, playToast, }) {
        const doneRef = useRef(done);
        const playToastRef = useRef(playToast);
        const onRemovedRef = useRef(onRemoved);
        const removalHandledRef = useRef(false);
        doneRef.current = done;
        playToastRef.current = playToast;
        onRemovedRef.current = onRemoved;
        useEffect(() => {
            if (isIn) {
                playToastRef.current();
                return;
            }
            if (removalHandledRef.current) {
                return;
            }
            removalHandledRef.current = true;
            /*
             * Remove the toast from Toastify's store first.
             *
             * Calling `done()` is the important part that the
             * default CSS transition would normally perform after
             * its exit animation.
             */
            doneRef.current();
            /*
             * Only resolve the dialog after Toastify no longer
             * considers this toast active. This prevents a second
             * showDialog() from racing the previous toast.
             */
            onRemovedRef.current();
        }, [isIn]);
        return _jsx(_Fragment, { children: children });
    }
    return ImmediateTransition;
}
function DialogToastContent({ options, toastProps, onResult, }) {
    const rootRef = useRef(null);
    const previouslyFocusedElementRef = useRef(null);
    const finishedRef = useRef(false);
    const { title, body, actions, tone = 'default', role = 'dialog', dismissValue, closeOnEscape = true, closeOnBackdrop = true, className = '', } = options;
    const { closeToast } = toastProps;
    const finish = useCallback((value) => {
        if (finishedRef.current) {
            return;
        }
        finishedRef.current = true;
        /*
         * Store the result before requesting Toastify to close.
         * Toastify's onClose callback can run synchronously when
         * closeToast() is called.
         */
        onResult(value);
        closeToast();
    }, [
        closeToast,
        onResult,
    ]);
    const dismiss = useCallback(() => {
        finish(dismissValue ?? null);
    }, [
        dismissValue,
        finish,
    ]);
    useEffect(function focusEffect() {
        previouslyFocusedElementRef.current = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        const root = rootRef.current;
        if (root === null) {
            return;
        }
        const autoFocusElement = root.querySelector('[data-dialog-autofocus="true"]');
        const firstFocusableElement = root.querySelector(focusableSelector) /*
         * Focus the explicitly requested action first,
         * otherwise the first focusable control.
         *
         * The dialog itself is the final fallback.
         */;
        (autoFocusElement
            ?? firstFocusableElement
            ?? root).focus();
        function handleKeyDown(event) {
            const dialog = rootRef.current;
            if (dialog === null) {
                return;
            }
            if (event.key === 'Escape'
                && closeOnEscape) {
                event.preventDefault();
                event.stopPropagation();
                dismiss();
                return;
            }
            if (event.key !== 'Tab') {
                return;
            }
            const focusableElements = Array.from(dialog.querySelectorAll(focusableSelector));
            if (focusableElements.length === 0) {
                event.preventDefault();
                dialog.focus();
                return;
            }
            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];
            const activeElement = document.activeElement;
            /*
             * If focus somehow escaped the dialog,
             * bring it back into the focus trap.
             */
            if (activeElement === null
                || !dialog.contains(activeElement)) {
                event.preventDefault();
                if (event.shiftKey) {
                    lastElement?.focus();
                }
                else {
                    firstElement?.focus();
                }
                return;
            }
            if (event.shiftKey
                && (activeElement
                    === firstElement
                    || activeElement === dialog)) {
                event.preventDefault();
                lastElement?.focus();
                return;
            }
            if (!event.shiftKey
                && activeElement === lastElement) {
                event.preventDefault();
                firstElement?.focus();
            }
        }
        document.addEventListener('keydown', handleKeyDown);
        return function cleanup() {
            document.removeEventListener('keydown', handleKeyDown);
            const previousElement = previouslyFocusedElementRef.current;
            if (previousElement !== null
                && document.contains(previousElement)) {
                previousElement.focus();
            }
        };
    }, [
        closeOnEscape,
        dismiss,
    ]);
    return (_jsx(Backdrop, { closeOnBackdrop: closeOnBackdrop, onDismiss: dismiss, children: _jsxs("div", { ref: rootRef, tabIndex: -1, role: role, "aria-modal": "true", "aria-labelledby": DIALOG_TITLE_ID, "aria-describedby": body === undefined
                ? undefined
                : DIALOG_BODY_ID, className: `
					w-full
					max-w-md
					rounded-sm
					text-black
					shadow-[0_3px_12px_rgba(0,0,0,0.22)]
					bg-background-main
					${className}
				`, children: [_jsxs("div", { className: "\n\t\t\t\t\t\tpx-4\n\t\t\t\t\t\tpb-2\n\t\t\t\t\t\tpt-4\n\t\t\t\t\t\tsm:px-5\n\t\t\t\t\t\tsm:pt-5\n\t\t\t\t\t", children: [_jsx("div", { id: DIALOG_TITLE_ID, className: `
							font-medium
							leading-6
							${tone === 'danger' ? 'text-error' : ''}
						`, children: title }), body !== undefined && (_jsx("div", { id: DIALOG_BODY_ID, className: "\n\t\t\t\t\t\t\t\tmt-2\n\t\t\t\t\t\t\t\tleading-6\n\t\t\t\t\t\t\t", children: body }))] }), _jsx("div", { className: "\n\t\t\t\t\t\tflex\n\t\t\t\t\t\tflex-nowrap\n\t\t\t\t\t\titems-center\n\t\t\t\t\t\tgap-2\n\t\t\t\t\t\tborder-t\n\t\t\t\t\t\tborder-tym-component-border-default\n\t\t\t\t\t\tpx-4\n\t\t\t\t\t\tpb-4\n\t\t\t\t\t\tpt-3\n\t\t\t\t\t\tsm:px-5\n\t\t\t\t\t\tsm:pb-5\n\t\t\t\t\t", children: actions.map((action, index) => (_jsx(Button, { position: "none", className: index === actions.length - 1
                            ? 'ml-auto w-auto!'
                            : 'w-auto!', variant: action.variant ?? 'primary', buttonClassName: action.autoFocus
                            ? 'focus:ring-2 focus:ring-offset-2 focus:ring-tym-primary'
                            : '', disabled: action.disabled, "data-dialog-autofocus": action.autoFocus
                            ? 'true'
                            : undefined, onClick: () => {
                            finish(action.value);
                        }, children: action.label }, action.value))) })] }) }));
}
let dialogIsOpen = false;
export async function showDialog(options) {
    if (dialogIsOpen) {
        return Promise.reject(new Error('Another application dialog is already open.'));
    }
    dialogIsOpen = true;
    /*
     * Result selected by an action, Escape, or backdrop.
     *
     * It remains null if Toastify/the application dismisses
     * the toast externally.
     */
    let result = null;
    /*
     * Once Toastify has begun closing externally we don't
     * allow a late click from the still-mounted DOM node to
     * replace the result.
     */
    let closeRequested = false;
    let settled = false;
    const promise = new Promise((resolve, reject) => {
        function resolveAfterRemoval() {
            if (settled) {
                return;
            }
            settled = true;
            resolve(result);
        }
        const ImmediateTransition = createImmediateTransition(resolveAfterRemoval);
        const toastOptions = {
            toastId: DIALOG_TOAST_ID,
            containerId: DIALOG_CONTAINER_ID,
            autoClose: false,
            closeButton: false,
            closeOnClick: false,
            draggable: false,
            /*
             * There is no Toastify progress UI in this
             * application dialog.
             */
            hideProgressBar: true,
            customProgressBar: true,
            icon: false,
            role: 'presentation',
            /*
             * Critical when importing
             * react-toastify/unstyled.
             *
             * Do not use Toastify's CSS-based default
             * transition here.
             */
            transition: ImmediateTransition,
            onClose() {
                /*
                 * In current Toastify versions, onClose
                 * happens when removal is requested, not
                 * after the exit transition has finished.
                 *
                 * Therefore this callback must NOT resolve
                 * the dialog promise.
                 */
                closeRequested = true;
            },
        };
        try {
            toast(toastProps => (_jsx(DialogToastContent, { options: options, toastProps: toastProps, onResult: value => {
                    /*
                     * For our own action/backdrop/
                     * Escape path this executes
                     * immediately before
                     * closeToast().
                     */
                    if (closeRequested) {
                        return;
                    }
                    result = value;
                } })), toastOptions);
        }
        catch (error) {
            settled = true;
            reject(error);
        }
    });
    return promise.finally(() => {
        dialogIsOpen = false;
    });
}
export function DialogContainer() {
    return (_jsx(ToastContainer, { containerId: DIALOG_CONTAINER_ID, position: "top-center", autoClose: false, closeButton: false, hideProgressBar: true, customProgressBar: true, newestOnTop: true, closeOnClick: false, draggable: false, pauseOnHover: false, pauseOnFocusLoss: false, limit: 1, role: "presentation", className: "\n\t\t\t\tpointer-events-none\n\t\t\t\tfixed!\n\t\t\t\tinset-0!\n\t\t\t\tz-9999!\n\t\t\t\tm-0!\n\t\t\t\tw-full!\n\t\t\t\tp-0!\n\t\t\t", toastClassName: "\n\t\t\t\tpointer-events-none\n\t\t\t\tm-0!\n\t\t\t\tw-0!\n\t\t\t\tmax-w-none!\n\t\t\t\tmin-h-0!\n\t\t\t\tbg-transparent!\n\t\t\t\tp-0!\n\t\t\t\tshadow-none!\n\t\t\t" }));
}
//# sourceMappingURL=Dialog.js.map